// Modules
import { useMemo } from "react";
import useSWR from "swr";
// Api
import api from "@/helpers/api";
// Types
import { PatientNotification } from "@/lib/types/notifications";
import { isEligibleForFeedback } from "@/helpers/patient";

type NotificationHookResult = {
    notifications: PatientNotification[];
    refreshNotifications: () => void;
    isValidating: boolean;
    error: any;
};

export const PatientNotificationsSWR = (patient?: any | null, events?: any): NotificationHookResult => {
    const fetcher = async (url: string): Promise<PatientNotification[]> => {
        const response = await api.get({ path: url });
        return response.data;
    };

    const {
        data: notifications = [],
        mutate: refreshNotifications,
        error,
        isValidating
    } = useSWR<PatientNotification[]>("/patient/notifications", fetcher, {
        fallbackData: [],
        revalidateOnFocus: true,
        refreshInterval: 60000
    });

    /**
     * Operation: FEEDBACK NOTIFICATION INJECTION
     * ------------------------------------------
     * Purpose:
     * Since feedback notifications aren't currently stored in the database,
     * we manually inject them into the notifications array at runtime.
     *
     * Logic:
     * 1. Check if the patient is eligible for feedback.
     * 2. If eligible, append a hardcoded feedback notification object to the fetched notifications.
     * 3. Otherwise, return the notifications as-is.
     */

    const isFeedbackEligible = useMemo(() => {
        if (!patient) return false;
        return isEligibleForFeedback({
            events,
            feedback: patient.therapy_program?.feedback,
            therapyStartDate: patient.therapy_program?.session?.start_date,
            shouldConsiderPostponed: false
        });
    }, [events, patient]);

    const combinedNotifications = useMemo(() => {
        if (!isFeedbackEligible || !patient) return notifications;
        const feedbackNotification: PatientNotification = {
            id: patient._id,
            type: "feedback",
            priority: 2
        };
        return [...notifications, feedbackNotification];
    }, [notifications, isFeedbackEligible, patient]);

    return {
        notifications: combinedNotifications,
        refreshNotifications,
        isValidating,
        error
    };
};
